// Monta o Tech Radar a partir do catálogo, sem lista mantida à mão: as tecnologias saem das
// tags dos Components (as do template "Aplicação genérica completa" vêm das imagens base do
// Dockerfile, ex.: dotnet, csharp, go) e a infraestrutura, dos Resources da plataforma.
// Lógica pura (sem React nem chamada de rede) para poder testar.

export type RadarComponent = { name: string; tags?: string[]; lifecycle?: string };
export type RadarResource = { name: string; title?: string; description?: string; system?: string };

export const QUADRANTS = [
  { id: 'languages', name: 'Languages' },
  { id: 'frameworks', name: 'Frameworks' },
  { id: 'infrastructure', name: 'Infrastructure' },
  { id: 'process', name: 'Process' },
];

export const RINGS = [
  { id: 'adopt', name: 'ADOPT', color: '#5BA300' },
  { id: 'trial', name: 'TRIAL', color: '#009EB0' },
  { id: 'assess', name: 'ASSESS', color: '#C7BA00' },
  { id: 'hold', name: 'HOLD', color: '#E09B96' },
];

// Só tag que é uma tecnologia conhecida vira item do radar: tags de negócio ("pagamentos") ou
// de versão ("net80") não são tecnologia e ficariam como ruído. As chaves são as tags que o
// template sugere a partir do Dockerfile (parseDockerfileSuggestions.ts); uma tag nova só entra
// aqui.
const TECHNOLOGIES: Record<string, { title: string; quadrant: string }> = {
  go: { title: 'Go', quadrant: 'languages' },
  csharp: { title: 'C#', quadrant: 'languages' },
  nodejs: { title: 'Node.js', quadrant: 'languages' },
  python: { title: 'Python', quadrant: 'languages' },
  java: { title: 'Java', quadrant: 'languages' },
  rust: { title: 'Rust', quadrant: 'languages' },
  ruby: { title: 'Ruby', quadrant: 'languages' },
  php: { title: 'PHP', quadrant: 'languages' },
  elixir: { title: 'Elixir', quadrant: 'languages' },
  dotnet: { title: '.NET', quadrant: 'frameworks' },
  nginx: { title: 'NGINX', quadrant: 'infrastructure' },
  'web-server': { title: 'Servidor web (httpd, Caddy, lighttpd)', quadrant: 'infrastructure' },
};

// Anel pelo ciclo de vida das aplicações que usam a tecnologia: em uso real (production) é
// ADOPT; só em teste (experimental, ou qualquer outro valor) é TRIAL; só em aplicações que
// serão desativadas (deprecated) é HOLD.
export function ringForLifecycles(lifecycles: string[]): 'adopt' | 'trial' | 'hold' {
  if (lifecycles.includes('production')) return 'adopt';
  if (lifecycles.length > 0 && lifecycles.every(item => item === 'deprecated')) return 'hold';
  return 'trial';
}

export type RadarItem = {
  key: string;
  id: string;
  quadrant: string;
  title: string;
  description: string;
  timeline: { date: Date; ringId: string; description: string }[];
  links?: { url: string; title: string }[];
};

export function radarEntries(
  components: RadarComponent[],
  resources: RadarResource[],
  now: Date = new Date(),
): RadarItem[] {
  const entries: RadarItem[] = [];

  const usage = new Map<string, { name: string; lifecycle: string }[]>();
  for (const component of components) {
    const lifecycle = component.lifecycle ?? 'experimental';
    for (const tag of new Set((component.tags ?? []).map(item => item.toLowerCase()))) {
      if (!TECHNOLOGIES[tag]) continue;
      usage.set(tag, [...(usage.get(tag) ?? []), { name: component.name, lifecycle }]);
    }
  }
  for (const [tag, users] of usage) {
    const technology = TECHNOLOGIES[tag];
    const text = `Usada por ${users.length} aplicação(ões): ${[...users]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(user => `${user.name} (${user.lifecycle})`)
      .join(', ')}.`;
    entries.push({
      key: tag,
      id: tag,
      quadrant: technology.quadrant,
      title: technology.title,
      description: text,
      timeline: [{ date: now, ringId: ringForLifecycles(users.map(user => user.lifecycle)), description: text }],
    });
  }

  // Serviços compartilhados da plataforma (catalog/platform/plataforma.yaml): o padrão do
  // ambiente, então ADOPT.
  for (const resource of resources) {
    if (resource.system !== 'plataforma') continue;
    const text = resource.description
      ? `Serviço da plataforma. ${resource.description}`
      : 'Serviço compartilhado da plataforma.';
    entries.push({
      key: `resource-${resource.name}`,
      id: `resource-${resource.name}`,
      quadrant: 'infrastructure',
      title: resource.title ?? resource.name,
      description: text,
      timeline: [{ date: now, ringId: 'adopt', description: text }],
      links: [{ url: `/catalog/default/resource/${resource.name}`, title: 'Ver no catálogo' }],
    });
  }

  return entries.sort((a, b) => a.title.localeCompare(b.title));
}
